<template>
    <div>
        <div class="text-sm flex justify-between">
            <p class="font-medium text-iron-700">
                {{ $t("event_types.booking_page.link") }}
            </p>

            <button
                class="font-medium text-z-primary-600"
                @click="copyBookingPageLink"
            >
                <span v-if="copied"> {{ $t("copied") }}</span>
                <span v-else> {{ $t("copy_link") }}</span>
            </button>
        </div>

        <InputWithPrefix
            :model-value="eventTypeUrlName"
            :variant="variant"
            @update:model-value="updateValue"
        >
            <template #prefix>
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger class="h-10 inline-flex shrink-0">
                            <span
                                class="h-10 rounded-l-md border-iron-300 bg-iron-50 px-3 text-iron-600 sm:text-sm inline-flex shrink-0 items-center border border-r-0"
                                >{{ shortenPrefix }}</span
                            >
                        </TooltipTrigger>
                        <TooltipContent>
                            {{ fullUrl }}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </template>
        </InputWithPrefix>

        <div v-if="error" class="mt-2 text-sm text-error-500">
            {{ error.reason }}
        </div>

        <div v-if="dirty" class="flex justify-end">
            <Button :disabled="!isValid" @click="save">{{ $t("save") }}</Button>
        </div>
    </div>
</template>

<script setup lang="ts">
import InputWithPrefix from "../input-with-prefix/InputWithPrefix.vue";
import { computed, ref } from "vue";
import { Button } from "../button";
import axios from "axios";
import route from "ziggy-js";
import { router } from "@inertiajs/vue3";
import { useClipboard } from "@vueuse/core";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { useI18n } from "vue-i18n";

const dirty = ref(false);

const error = ref<{ reason: string }>({
    reason: "",
});

interface Props {
    teamSlug: string;
    eventTypeId: number;
    slug: string;
    publicUrl: string;
}

const props = withDefaults(defineProps<Props>(), {});

const { t } = useI18n();

const { copy, copied } = useClipboard();

const eventTypeUrlName = ref(props.slug);

const variant = computed(() => {
    return error.value.reason ? "error" : "default";
});

const isValid = computed(() => {
    return !!error.value.reason === false;
});

const urlPrefix = computed(() => {
    try {
        const url = new URL(props.publicUrl);
        const pathParts = url.pathname.split("/").filter(Boolean);
        // Remove the last part (the slug) to get the prefix path
        const prefixParts = pathParts.slice(0, -1);
        const prefixPath = prefixParts.join("/");
        const full = prefixPath ? `${url.host}/${prefixPath}/` : `${url.host}/`;
        return { full, host: url.host, path: prefixPath };
    } catch {
        return {
            full: `zapti.me/${props.teamSlug}/`,
            host: "zapti.me",
            path: props.teamSlug,
        };
    }
});

const shortenPrefix = computed(() => {
    const full = urlPrefix.value.full;
    if (full.length > 20) {
        return full.substring(0, 17) + ".../";
    }
    return full;
});

const alphanumericDashPattern = /^[a-zA-Z0-9-]+$/;

const fullUrl = computed(() => {
    try {
        const url = new URL(props.publicUrl);
        const pathParts = url.pathname.split("/").filter(Boolean);
        // Replace the last part (original slug) with the current edited slug
        pathParts[pathParts.length - 1] = eventTypeUrlName.value;
        return `${url.origin}/${pathParts.join("/")}`;
    } catch {
        return `https://zapti.me/${props.teamSlug}/${eventTypeUrlName.value}`;
    }
});

function copyBookingPageLink() {
    copy(fullUrl.value);
}

function validateSlug(newSlug: string) {
    newSlug = newSlug.replaceAll(" ", "-");

    if (newSlug.length > 64) {
        error.value.reason = t("event_types.booking_page.link_length");
    } else if (!alphanumericDashPattern.test(newSlug)) {
        error.value.reason = t("event_types.booking_page.link_special_chars");
    } else {
        error.value = { reason: "" };
    }

    return newSlug;
}

const updateValue = (value: string) => {
    eventTypeUrlName.value = validateSlug(value);
    dirty.value = true;
};

async function save() {
    if (error.value.reason) {
        return;
    }

    try {
        const res = await axios.post(
            route("app.event-types.slugs.check", {
                eventType: props.eventTypeId,
            }),
            {
                slug: eventTypeUrlName.value,
            },
        );

        if (res.data.data.slug === eventTypeUrlName.value) {
            router.put(
                route("app.event-types.slugs.update", {
                    eventType: props.eventTypeId,
                }),
                {
                    slug: eventTypeUrlName.value,
                },
                {
                    preserveScroll: true,
                    preserveState: true,
                },
            );
        } else {
            error.value.reason = t(
                "event_types.booking_page.link_alerady_exists",
            );
        }
        dirty.value = false;
    } catch (e) {
        // @ts-expect-error
        error.value.reason = e.response.data.message;
    }
}
</script>
